/**
 * Branded SPA error page — drop-in component for every SolanaDeads-fleet frontend.
 *
 * Brand is auto-detected from window.location.hostname (override with `brand` prop).
 * Code is one of: 403 | 404 | 413 | 429 | 500 | 502 | 503 | 504 (default 404).
 *
 * Usage (React Router catch-all):
 *   <Route path="*" element={<ErrorPage code={404} />} />
 *
 * Usage (error boundary / API failure):
 *   <ErrorPage code={502} message="The mint API didn't respond in time" />
 *
 * The component injects its own <style> tag once per render — no external CSS needed.
 *
 * Source of truth for brand profiles: launchpad/nginx/error-pages/profiles.json.
 * Keep BRANDS below in sync if profiles.json changes.
 */

import React from 'react';

const BRANDS = {
  gravemint: {
    name: 'GraveMint',
    logo: 'https://images.solanadeads.com/gravemint.png',
    accentFrom: '#a855f7', accentMid: '#6366f1', accentTo: '#8b5cf6',
    btnFrom: '#8b5cf6', btnTo: '#6366f1',
    home: 'https://gravemint.io',
  },
  gravemarket: {
    name: 'Gravemarket',
    logo: 'https://images.solanadeads.com/gravemarket.png',
    accentFrom: '#ef4444', accentMid: '#dc2626', accentTo: '#b91c1c',
    btnFrom: '#ef4444', btnTo: '#b91c1c',
    home: 'https://gravemarket.io',
  },
  gravewave: {
    name: 'GraveWave',
    logo: 'https://images.solanadeads.com/gravewave.png',
    accentFrom: '#a855f7', accentMid: '#7c3aed', accentTo: '#6d28d9',
    btnFrom: '#7c3aed', btnTo: '#6d28d9',
    home: 'https://gravewave.io',
  },
  graveforge: {
    name: 'GraveForge',
    logo: 'https://images.solanadeads.com/graveforge.png',
    accentFrom: '#fb923c', accentMid: '#ea580c', accentTo: '#c2410c',
    btnFrom: '#ea580c', btnTo: '#c2410c',
    home: 'https://graveforge.io',
  },
  gravekeeper: {
    name: 'GraveKeeper',
    logo: 'https://images.solanadeads.com/gravekeeper.png',
    accentFrom: '#e2e8f0', accentMid: '#94a3b8', accentTo: '#64748b',
    btnFrom: '#475569', btnTo: '#1e293b',
    home: 'https://gravekeeper.io',
  },
  gravestake: {
    name: 'GraveStake',
    logo: 'https://images.solanadeads.com/gravestake.png',
    accentFrom: '#ef4444', accentMid: '#dc2626', accentTo: '#b91c1c',
    btnFrom: '#ef4444', btnTo: '#b91c1c',
    home: 'https://gravestake.io',
  },
  solanadeads: {
    name: 'SolanaDeads',
    logo: 'https://images.solanadeads.com/sdlogo.png',
    accentFrom: '#ef4444', accentMid: '#dc2626', accentTo: '#b91c1c',
    btnFrom: '#ef4444', btnTo: '#b91c1c',
    home: 'https://www.solanadeads.com',
  },
};

const HOSTNAME_TO_BRAND = {
  'gravemint.io': 'gravemint',
  'gravemarket.io': 'gravemarket',
  'gravewave.io': 'gravewave',
  'graveforge.io': 'graveforge',
  'gravekeeper.io': 'gravekeeper',
  'gravestake.io': 'gravestake',
};

function detectBrand() {
  if (typeof window === 'undefined') return BRANDS.solanadeads;
  const host = window.location.hostname.toLowerCase();
  for (const [apex, key] of Object.entries(HOSTNAME_TO_BRAND)) {
    if (host === apex || host.endsWith('.' + apex)) return BRANDS[key];
  }
  return BRANDS.solanadeads;
}

const CODES = {
  403: {
    title: 'Forbidden',
    icon: '🔒',
    body: "You don't have permission to access this resource. If you think this is a mistake, head home and try a different path.",
    refresh: 0,
    accent: 'brand',
  },
  404: {
    title: 'Page Not Found',
    icon: '💀',
    body: "The page you're looking for has vanished into the digital abyss. It may have been moved, deleted, or never existed in the first place.",
    refresh: 0,
    accent: 'brand',
  },
  413: {
    title: 'Payload Too Large',
    icon: '📦',
    body: 'The file or request you sent exceeds our size limit. Try a smaller upload, or split it into pieces.',
    refresh: 0,
    accent: 'brand',
  },
  429: {
    title: 'Too Many Requests',
    icon: '🚦',
    body: "You're sending requests faster than we can handle. Take a breath and try again in a moment.",
    refresh: 20,
    accent: 'warn',
  },
  500: {
    title: 'Server Error',
    icon: '⚠️',
    body: 'Something went wrong on our end. Our team has been notified and is working to fix the issue.',
    refresh: 0,
    accent: 'danger',
  },
  502: {
    title: 'Bad Gateway',
    icon: '🔌',
    body: "We're having trouble connecting to our servers. This is usually temporary and resolves quickly.",
    refresh: 10,
    accent: 'warn',
  },
  503: {
    title: 'Under Maintenance',
    icon: '⚙️',
    body: "We're performing scheduled maintenance to improve your experience. This won't take long.",
    refresh: 30,
    accent: 'brand',
  },
  504: {
    title: 'Gateway Timeout',
    icon: '⏱️',
    body: 'The upstream took too long to respond. This is usually temporary — give it a moment and try again.',
    refresh: 15,
    accent: 'warn',
  },
};

const ACCENT_RAMPS = {
  brand: b => ({ from: b.accentFrom, mid: b.accentMid, to: b.accentTo, glow: b.accentMid }),
  warn:  () => ({ from: '#f59e0b', mid: '#d97706', to: '#b45309', glow: '#f59e0b' }),
  danger:() => ({ from: '#ef4444', mid: '#dc2626', to: '#b91c1c', glow: '#ef4444' }),
};

export default function ErrorPage({ code = 404, message, brand, homeUrl }) {
  const b = brand && BRANDS[brand] ? BRANDS[brand] : detectBrand();
  const info = CODES[code] || CODES[500];
  const ramp = ACCENT_RAMPS[info.accent](b);
  const home = homeUrl || b.home;

  React.useEffect(() => {
    if (info.refresh > 0) {
      const t = window.setTimeout(() => window.location.reload(), info.refresh * 1000);
      return () => window.clearTimeout(t);
    }
  }, [info.refresh]);

  return (
    <div className="sd-err-root">
      <style>{`
        .sd-err-root, .sd-err-root * { box-sizing: border-box; margin: 0; padding: 0; }
        .sd-err-root {
          position: fixed; inset: 0;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          background: linear-gradient(135deg, #0a0a0f 0%, #1a1a2e 50%, #0f0f1a 100%);
          display: flex; align-items: center; justify-content: center;
          color: #fff; overflow: hidden; z-index: 9999;
        }
        .sd-err-container { text-align: center; padding: 2rem; max-width: 600px; }
        .sd-err-logo { margin-bottom: 2rem; }
        .sd-err-logo img { max-width: 180px; height: auto; }
        .sd-err-icon { font-size: 4rem; margin-bottom: 1.5rem; animation: sd-err-float 3s ease-in-out infinite; }
        .sd-err-code {
          font-size: 8rem; font-weight: 700;
          background: linear-gradient(135deg, ${ramp.from} 0%, ${ramp.mid} 50%, ${ramp.to} 100%);
          -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text;
          line-height: 1; margin-bottom: 1rem;
          text-shadow: 0 0 80px ${ramp.glow}80;
        }
        .sd-err-title { font-size: 1.75rem; font-weight: 600; margin-bottom: 1rem; color: #e2e8f0; }
        .sd-err-body { color: #94a3b8; font-size: 1.1rem; margin-bottom: 2rem; line-height: 1.6; }
        .sd-err-btn {
          display: inline-flex; align-items: center; gap: 0.5rem;
          padding: 0.875rem 2rem;
          background: linear-gradient(135deg, ${b.btnFrom} 0%, ${b.btnTo} 100%);
          color: #fff; text-decoration: none; border: none; cursor: pointer;
          border-radius: 0.75rem; font-weight: 600; font-size: 1rem;
          transition: transform 0.3s ease, box-shadow 0.3s ease;
          box-shadow: 0 4px 20px ${b.btnFrom}66;
          margin: 0 0.5rem;
        }
        .sd-err-btn:hover { transform: translateY(-2px); box-shadow: 0 6px 30px ${b.btnFrom}99; }
        .sd-err-btn-secondary { background: rgba(255,255,255,0.1); box-shadow: none; border: 1px solid rgba(255,255,255,0.2); }
        .sd-err-btn-secondary:hover { background: rgba(255,255,255,0.15); box-shadow: none; }
        .sd-err-btns { display: flex; justify-content: center; flex-wrap: wrap; gap: 1rem; }
        .sd-err-refresh { margin-top: 1rem; font-size: 0.85rem; color: #64748b; }
        .sd-err-glow { position: fixed; width: 400px; height: 400px; border-radius: 50%; filter: blur(100px); opacity: 0.15; pointer-events: none; }
        .sd-err-glow-1 { background: ${ramp.from}; top: -100px; right: -100px; }
        .sd-err-glow-2 { background: ${ramp.to}; bottom: -100px; left: -100px; }
        @keyframes sd-err-float { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-10px); } }
      `}</style>
      <div className="sd-err-glow sd-err-glow-1" />
      <div className="sd-err-glow sd-err-glow-2" />
      <div className="sd-err-container">
        <div className="sd-err-logo">
          <a href={home}>
            <img src={b.logo} alt={b.name} width="180" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
          </a>
        </div>
        <div className="sd-err-icon" aria-hidden>{info.icon}</div>
        <div className="sd-err-code">{code}</div>
        <h1 className="sd-err-title">{info.title}</h1>
        <p className="sd-err-body">{message || info.body}</p>
        <div className="sd-err-btns">
          <a href={home} className="sd-err-btn"><span>← Return Home</span></a>
          {code !== 404 && code !== 403 && (
            <button type="button" onClick={() => window.location.reload()} className="sd-err-btn sd-err-btn-secondary">
              <span>↻ Try Again</span>
            </button>
          )}
        </div>
        {info.refresh > 0 && (
          <p className="sd-err-refresh">This page will automatically refresh in {info.refresh} seconds.</p>
        )}
      </div>
    </div>
  );
}
